import type {ReactElement, ReactNode} from 'react';
import React from 'react';
import {Icon} from '@Components/icon-v2';
import {Text, TextSize} from '@Components/text';
import {getOAuthClientMessage} from '@Libraries/login-signup-library';
import {resetNotificationBannerAnimation} from '@Components/login-page/login-page-reducer';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {useIsLoginToProceedMessage, useLoginPageAnimateNotificationBanner, useLoginPageAuthType, useLoginPageErrorMessage, useLoginPageShowError} from '@Components/login-page/login-page.hooks';
import {useUpdateErrorStates} from '@/hooks/login-signup/useUpdateErrorStates';
import {useAppDispatch} from '@/hooks';
import styles from './notification-banner.module.scss';
import {AuthTestId} from '@/test-ids/auth-test-id';

interface NotificationBannerProps {
  showOauthClientMessage?: boolean;
  clientName?: string;
}

function NotificationBanner({showOauthClientMessage = false, clientName = ''}: NotificationBannerProps): ReactElement {
  const dispatch = useAppDispatch();
  const animateBanner = useLoginPageAnimateNotificationBanner();
  const showError = useLoginPageShowError();
  const errorMessage = useLoginPageErrorMessage();
  const isLoginToProceedMessage = useIsLoginToProceedMessage();
  const authType = useLoginPageAuthType();
  const {hideErrors} = useUpdateErrorStates();

  /**
   * The error variant has no leading icon and a dismiss control. The login-to-proceed variant keeps its
   * informational icon and stays non-dismissable — only the error state has an action
   * (`hideErrors`) that can clear it.
   */
  const isErrorVariant = (): boolean => {
    return showError && !isLoginToProceedMessage;
  };

  /**
   * The OAuth panels name the app being connected to. It is page context rather than a notice, so it carries
   * no dismiss control and, unlike the other informational variants, no icon. A real
   * failure still takes the banner over.
   */
  const isOauthClientVariant = (): boolean => {
    return showOauthClientMessage && !showError;
  };

  const getLeadingIcon = (): ReactNode => {
    if (isErrorVariant() || isOauthClientVariant()) {
      return null;
    }
    return <Icon icon="icon-info" size={IconSize.SIZE_ICON_20} className={getIconClassName()} />;
  };

  const getDismissButton = (): ReactNode => {
    if (!isErrorVariant()) {
      return null;
    }
    return (
      <Icon
        ariaLabel={window.i18next.t('pmwjs_close')}
        className={styles.dismissIcon}
        icon="icon-close"
        isUsedAsButton
        onClick={hideErrors}
        shape={IconShape.SQUARE}
        size={IconSize.SIZE_ICON_16}
        type={IconType.TRANSPARENT}
      />
    );
  };

  const getContainerClasses = (): string => {
    let containerClasses = styles.errorMessageContainer;

    if (isLoginToProceedMessage) {
      containerClasses += ` ${styles.loginMessageContainer}`;
    }

    if (isOauthClientVariant()) {
      containerClasses += ` ${styles.oauthClientMessageContainer}`;
    }

    if (showError || isOauthClientVariant()) {
      return containerClasses;
    }
    return `${containerClasses} _hidden`;
  };

  const getMessageColorClass = (): string => {
    if (isLoginToProceedMessage || isOauthClientVariant()) {
      return styles.loginMessage;
    }
    return styles.errorText;
  };

  const getMessage = (): string => {
    return isOauthClientVariant() ? getOAuthClientMessage(authType, clientName) : errorMessage;
  };

  const getAnimationClasses = (): string => {
    if (animateBanner) {
      return 'fast shake animated';
    }
    return '';
  };

  const getIconClassName = (): string => {
    if (isLoginToProceedMessage) {
      return styles.loginMessageIcon;
    }

    return styles.errorMessageIcon;
  };

  const getStripClassName = (): string => {
    if (isLoginToProceedMessage || isOauthClientVariant()) {
      return styles.messageStrip;
    }

    return styles.errorStrip;
  };

  return (
    <div
      className={`${getContainerClasses()} flex-center bg-neutral-0 ${getAnimationClasses()}`}
      data-testid={AuthTestId.ERROR_BANNER}
      onAnimationEnd={() => {
        dispatch(resetNotificationBannerAnimation());
      }}
    >
      <div className={`${styles.stripContainer} ${getStripClassName()}`} />
      <div className={`flex-center ${styles.bannerBody}`}>
        {getLeadingIcon()}
        <Text size={TextSize.MD} dangerouslySetInnerHTML val={getMessage()} className={getMessageColorClass()} />
        {getDismissButton()}
      </div>
    </div>
  );
}

export default React.memo(NotificationBanner);
